// Move File Tool - Provides file and directory move/rename functions for workspace
// Extends WorkspaceTool - has access to appType and toolSystem

import { WorkspaceTool } from "../../base/WorkspaceTool.js";
import { logger } from "../../../../../../core/logger.js";

export class MoveFileTool extends WorkspaceTool {
  constructor() {
    super();
    this.toolName = 'moveFileTool';
  }
  /**
   * Move or rename a file or directory
   * @param {string} sourcePath - Path to the file or directory to move
   * @param {string} destinationPath - Destination path (can be new name or new location)
   * @param {boolean} [overwrite=false] - Whether to overwrite existing destination
   * @returns {Promise<Object>} Result with operation status
   */
  async moveFile(sourcePath, destinationPath, overwrite = false) {
    try {
      // Validate source path
      if (!sourcePath || typeof sourcePath !== "string") {
        return this.returnError({}, "Valid source path required");
      }

      // Validate destination path
      if (!destinationPath || typeof destinationPath !== "string") {
        return this.returnError({}, "Valid destination path required");
      }

      // Route based on app type
      if (this.appType === "browser") {
        return await this.moveFileForBrowser(
          sourcePath,
          destinationPath,
          overwrite
        );
      }
      if (this.appType === "local") {
        return await this.moveFileForLocal(
          sourcePath,
          destinationPath,
          overwrite
        );
      }
      return this.returnError(
        {},
        `App type '${this.appType}' not yet implemented for moveFile`
      );
    } catch (error) {
      logger.error(
        {
          tags: "tools|movefile|error",
          color1: "red",
        },
        `Failed to move: ${sourcePath} -> ${destinationPath}`,
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Move file or directory for browser apps using Lightning FS
   */
  async moveFileForBrowser(sourcePath, destinationPath, overwrite) {
    const fs = this.fs?.promises;
    if (!fs) {
      return this.returnError(
        {},
        "Lightning FS not available - browser app filesystem not initialized"
      );
    }

    // Normalize paths - Lightning FS uses absolute paths
    const normalizedSourcePath = sourcePath.startsWith("/")
      ? sourcePath
      : `/${sourcePath}`;
    const normalizedDestPath = destinationPath.startsWith("/")
      ? destinationPath
      : `/${destinationPath}`;

    // Prepend /files/ to the filesystem paths
    const sourceFilesystemPath = `/files${normalizedSourcePath}`;
    const destFilesystemPath = `/files${normalizedDestPath}`;

    // Validate move operation for safety
    if (normalizedSourcePath === normalizedDestPath) {
      return this.returnError(
        {},
        `Cannot move item to itself: ${sourcePath}`
      );
    }

    // Check if destination path would be inside source path (for directories)
    if (normalizedDestPath.startsWith(normalizedSourcePath + '/')) {
      return this.returnError(
        {},
        `Cannot move directory into itself or its subdirectories: ${sourcePath} → ${destinationPath}`
      );
    }

    try {
      // Check if source exists
      const sourceStats = await fs.stat(sourceFilesystemPath);
      const sourceIsDir =
        typeof sourceStats.isDirectory === "function"
          ? sourceStats.isDirectory()
          : sourceStats.type === "dir";

      // Additional validation for directories: prevent moving into subdirectories
      if (sourceIsDir && destFilesystemPath.startsWith(sourceFilesystemPath + '/')) {
        return this.returnError(
          {},
          `Cannot move directory into one of its subdirectories: ${sourcePath} → ${destinationPath}`
        );
      }

      // Check if destination exists
      let destExists = false;
      let destIsDir = false;
      try {
        const destStats = await fs.stat(destFilesystemPath);
        destExists = true;
        destIsDir =
          typeof destStats.isDirectory === "function"
            ? destStats.isDirectory()
            : destStats.type === "dir";
      } catch (error) {
        if (error.code !== "ENOENT") {
          throw error;
        }
        // Destination doesn't exist, which is fine
      }

      // Validate move operation
      if (destExists) {
        // Destination exists - check if types match
        if (sourceIsDir !== destIsDir) {
          return this.returnError(
            {},
            `Cannot move ${sourceIsDir ? "directory" : "file"} to ${destIsDir ? "directory" : "file"} - types must match`
          );
        }

        // Check overwrite permission
        if (!overwrite) {
          return this.returnError(
            {},
            `Destination already exists: ${destinationPath}. Use overwrite=true to overwrite`
          );
        }

        // Delete destination if overwrite is enabled
        if (destIsDir) {
          // For directories, we need to delete recursively
          await this.deleteDirectoryRecursive(destFilesystemPath, fs);
        } else {
          // For files, just unlink
          await fs.unlink(destFilesystemPath);
        }
      }

      // Ensure destination parent directory exists
      const destParentPath = destFilesystemPath
        .split("/")
        .slice(0, -1)
        .join("/");
      if (
        destParentPath &&
        destParentPath !== "/" &&
        destParentPath !== "/files"
      ) {
        try {
          await fs.stat(destParentPath);
        } catch (error) {
          if (error.code === "ENOENT") {
            // Create parent directories recursively
            await this.ensureDirectory(destParentPath, fs);
          } else {
            throw error;
          }
        }
      }

      // Perform the move/rename operation
      await fs.rename(sourceFilesystemPath, destFilesystemPath);

      // Sync changes with workspace manager to update UI
      if (this.workspaceManager?.syncOpenFiles) {
        await this.workspaceManager.syncOpenFiles([normalizedSourcePath, normalizedDestPath]);
      }

      logger.log(
        {
          tags: "tools|movefile|info",
          color1: "lime",
        },
        `Moved: ${sourceFilesystemPath} -> ${destFilesystemPath}`,
        {
          source: normalizedSourcePath,
          destination: normalizedDestPath,
          type: sourceIsDir ? "directory" : "file",
          overwrite,
        }
      );

      return this.returnSuccess(
        {
          source: normalizedSourcePath,
          destination: normalizedDestPath,
          type: sourceIsDir ? "directory" : "file",
          overwrite,
        },
        `${sourceIsDir ? "Directory" : "File"} moved successfully`
      );
    } catch (error) {
      if (error.code === "ENOENT") {
        return this.returnError({}, `Source not found: ${sourcePath}`);
      }
      if (error.code === "EXDEV") {
        return this.returnError(
          {},
          `Cannot move across different filesystems. Use copy and delete instead.`
        );
      }
      throw error;
    }
  }

  /**
   * Move or rename for local apps (Revolt Desktop + workspaceLocalAppAPI).
   */
  async moveFileForLocal(sourcePath, destinationPath, overwrite) {
    const api = this.getWorkspaceLocalAppApi();
    if (!api?.moveFile) {
      return this.returnError(
        {},
        "Local file moves require Revolt Desktop (workspaceLocalAppAPI.moveFile)."
      );
    }

    const root = this.getLocalWorkingDirectoryRoot();
    if (!root) {
      return this.returnError(
        {},
        "Local app has no working directory (settings.workingDirectoryPath)."
      );
    }

    const normalizedSourcePath = this.normalizeAndValidatePath(sourcePath);
    const normalizedDestPath = this.normalizeAndValidatePath(destinationPath);
    if (!normalizedSourcePath || !normalizedDestPath) {
      return this.returnError({}, "Invalid source or destination path");
    }

    if (normalizedSourcePath === normalizedDestPath) {
      return this.returnError(
        {},
        `Cannot move item to itself: ${sourcePath}`
      );
    }

    if (normalizedDestPath.startsWith(`${normalizedSourcePath}/`)) {
      return this.returnError(
        {},
        `Cannot move directory into itself or its subdirectories: ${sourcePath} → ${destinationPath}`
      );
    }

    const sourceRelativePath = normalizedSourcePath.replace(/^\/+/, "");
    const destRelativePath = normalizedDestPath.replace(/^\/+/, "");

    const res = await api.moveFile({
      rootPath: root,
      sourceRelativePath,
      destRelativePath,
      overwrite,
    });

    if (!res?.success) {
      return this.returnError({}, res?.error || "Failed to move on disk");
    }

    if (this.workspaceManager?.syncOpenFiles) {
      await this.workspaceManager.syncOpenFiles([
        normalizedSourcePath,
        normalizedDestPath,
      ]);
    }

    const type = res.type === "directory" ? "directory" : "file";

    logger.log(
      {
        tags: "tools|movefile|info",
        color1: "lime",
      },
      `Moved (local): ${normalizedSourcePath} -> ${normalizedDestPath}`,
      {
        source: normalizedSourcePath,
        destination: normalizedDestPath,
        type,
        overwrite,
      }
    );

    return this.returnSuccess(
      {
        source: normalizedSourcePath,
        destination: normalizedDestPath,
        type,
        overwrite,
      },
      `${type === "directory" ? "Directory" : "File"} moved successfully`
    );
  }

  /**
   * Recursively delete a directory and all its contents
   * @param {string} dirPath - Directory path to delete
   * @param {Object} fs - Filesystem promises interface
   */
  async deleteDirectoryRecursive(dirPath, fs) {
    try {
      const items = await fs.readdir(dirPath);

      for (const item of items) {
        const itemPath =
          dirPath === "/files" ? `/files/${item}` : `${dirPath}/${item}`;

        try {
          const stats = await fs.stat(itemPath);
          const isDir =
            typeof stats.isDirectory === "function"
              ? stats.isDirectory()
              : stats.type === "dir";

          if (isDir) {
            // Recursively delete subdirectory contents first
            await this.deleteDirectoryRecursive(itemPath, fs);
            // Then delete the subdirectory
            await fs.rmdir(itemPath);
          } else {
            // Delete file
            await fs.unlink(itemPath);
          }
        } catch (error) {
          logger.warn(
            {
              tags: "tools|movefile|delete",
              color1: "yellow",
            },
            `Failed to delete entry during move: ${itemPath}`,
            error
          );
          // Continue with other entries even if one fails
        }
      }

      // Delete the directory itself
      await fs.rmdir(dirPath);
    } catch (error) {
      logger.error(
        {
          tags: "tools|movefile|delete",
          color1: "red",
        },
        `Failed to delete directory during move: ${dirPath}`,
        error
      );
      throw error;
    }
  }

  /**
   * Ensure directory exists, creating parent directories as needed
   * @param {string} directoryPath - Directory path to ensure exists
   * @param {Object} fs - Filesystem promises interface
   */
  async ensureDirectory(directoryPath, fs) {
    if (!directoryPath || directoryPath === "/" || directoryPath === "/files") {
      return;
    }

    const segments = directoryPath.split("/").filter(Boolean);
    let currentPath = "";

    for (const segment of segments) {
      currentPath += `/${segment}`;
      try {
        await fs.stat(currentPath);
      } catch (error) {
        if (error.code === "ENOENT") {
          try {
            await fs.mkdir(currentPath);
          } catch (mkdirError) {
            // Directory might have been created in parallel; ignore
            if (mkdirError.code !== "EEXIST") {
              logger.warn(
                {
                  tags: "tools|movefile|mkdir",
                  color1: "yellow",
                },
                `Failed to create directory: ${currentPath}`,
                mkdirError
              );
            }
          }
        } else {
          throw error;
        }
      }
    }
  }
}
